import { useCallback, useEffect, useRef } from 'react'
import { apiGet, ApiError } from '../api/client'
import {
  trackCheckoutCompleted,
  trackCheckoutFailed,
  trackCheckoutOpened,
} from '../analytics/posthog'
import type { UserProfile } from '../types/user'

interface CheckoutUrlResponse {
  checkout_url: string
}

interface UseLemonSqueezyOptions {
  onCreditsUpdated?: () => void
  isSubscribed?: boolean
  planName?: string | null
  credits?: number
}

export function useLemonSqueezy({ onCreditsUpdated, isSubscribed, planName, credits }: UseLemonSqueezyOptions = {}) {
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null)
  // Snapshot of subscription state when polling starts
  const snapshotRef = useRef<{ isSubscribed?: boolean; planName?: string | null; credits?: number }>({})

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current)
      pollingRef.current = null
    }
  }, [])

  // Clean up on unmount
  useEffect(() => stopPolling, [stopPolling])

  const startPolling = useCallback(() => {
    stopPolling()
    snapshotRef.current = { isSubscribed, planName, credits }

    const pollFn = async () => {
      try {
        const profile = await apiGet<UserProfile>('/auth/me')
        const snap = snapshotRef.current
        // Detect any change: subscription status, plan, or credits
        if (
          profile.is_subscribed !== snap.isSubscribed ||
          profile.plan_name !== snap.planName ||
          profile.max_uploads_allowed !== snap.credits
        ) {
          // Closest client-side proof of a paid checkout (the webhook's
          // Purchase Success is the authoritative one, server-side)
          if (profile.is_subscribed && !snap.isSubscribed) {
            trackCheckoutCompleted(profile.plan_name)
          }
          stopPolling()
          onCreditsUpdated?.()
        }
      } catch (err) {
        // Stop polling on auth errors — token is invalid
        if (err instanceof ApiError && err.status === 401) {
          stopPolling()
        }
      }
    }

    pollingRef.current = setInterval(pollFn, 5000)

    // Stop after 2 minutes
    setTimeout(stopPolling, 120_000)
  }, [stopPolling, onCreditsUpdated, isSubscribed, planName, credits])

  // Refresh on tab visibility change (user returning from checkout)
  useEffect(() => {
    function handleVisibility() {
      if (document.visibilityState === 'visible' && pollingRef.current) {
        onCreditsUpdated?.()
      }
    }
    document.addEventListener('visibilitychange', handleVisibility)
    return () => document.removeEventListener('visibilitychange', handleVisibility)
  }, [onCreditsUpdated])

  const openCheckout = useCallback(async (planName: string, opts: { sameTab?: boolean } = {}) => {
    // The tab must be opened synchronously inside the click gesture — a
    // window.open issued after `await` is popup-blocked (Safari's default),
    // and the user sees a Subscribe button that silently does nothing.
    // sameTab: the automatic continuation after sign-in runs outside any
    // gesture, so it navigates this tab instead (Lemon's hosted page; the
    // configured return URL brings the user back — see IndexPage).
    const checkoutTab = opts.sameTab ? null : window.open('', '_blank')
    try {
      const data = await apiGet<CheckoutUrlResponse>(
        `/subscriptions/checkout-url?plan_name=${encodeURIComponent(planName)}`
      )
      trackCheckoutOpened(planName, opts.sameTab ? 'same-tab' : 'new-tab')
      if (checkoutTab) {
        checkoutTab.location.href = data.checkout_url
        startPolling()
        return
      }
      // Same-tab navigation always works (also the fallback when even the
      // synchronous window.open was blocked); a beat for the analytics queue
      setTimeout(() => window.location.assign(data.checkout_url), 150)
    } catch (err) {
      checkoutTab?.close()
      trackCheckoutFailed(planName, err instanceof Error ? err.message : 'unknown')
      throw err
    }
  }, [startPolling])

  return { openCheckout, startPolling }
}
